/* ============================================================

   FLOWERTRAIL — KX motion engine v2 (Koenigsegg-grade)

   GSAP + ScrollTrigger choreography for the whole storefront:

     · Cinzel intro veil, once per session

     · DIAGONAL page sweep — a gold hairline band leading a black

       panel from top-left to bottom-right on every internal

       navigation, continuing off the far corner on arrival

     · gold cursor (ring + core) that can NEVER strand the user

       cursor-less: the native cursor is only hidden while the

       ring is provably alive (html.kx-cursor-live)

     · clip-mask heading reveals, batched card rises, A+ unmasks

     · scroll-scrubbed parallax, magnetic CTAs, progress hairline

     · [data-kx-carousel] snap-strip helper

   Coexists with ft-motion.js (.ft-reveal untouched) and

   ft-zoom-guard.js (everything pauses mid pinch-zoom).

   Transforms, clip-path and opacity only — no layout thrash.

   ============================================================ */

(function () {

  'use strict';



  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var finePointer = window.matchMedia && window.matchMedia('(pointer: fine)').matches;

  var html = document.documentElement;



  function clearBootCovers() {

    html.classList.remove('kx-intro-pending', 'kx-enter');

  }



  if (reduce || !window.gsap) {

    clearBootCovers();

    return;

  }



  /* MOBILE FAST PATH: phones never initialize ScrollTrigger at all —

     it cost ~3.4s of throttled main-thread time. GSAP core stays

     (blanket/menu/carousel tweens are cheap transforms); reveals run

     on one IntersectionObserver + CSS transitions instead. */

  var mobileFast = (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) ||

                   window.innerWidth < 990;

  html.classList.add('kx-motion');



  /* ScrollTrigger loads DYNAMICALLY, desktop-only — phones never even

     fetch the file. Desktop scroll choreography initializes once it

     lands; everything else (blanket, menu, carousels, cursor, cheap

     mobile reveals) needs only GSAP core and runs immediately. */

  var stReady = false;

  var stQueue = [];

  function withST(fn) {

    if (mobileFast || lite) return;

    if (stReady) { fn(); return; }

    stQueue.push(fn);

  }

  function loadST() {

    if (mobileFast || lite || stReady || !window.FT_ST_URL) return;

    function arm() {

      if (stReady || !window.ScrollTrigger) return;

      gsap.registerPlugin(ScrollTrigger);

      ScrollTrigger.config({ ignoreMobileResize: true });

      stReady = true;

      var q = stQueue.splice(0);

      q.forEach(function (fn) { safe(fn); });

      ScrollTrigger.refresh();

    }

    if (window.ScrollTrigger) { arm(); return; }

    var s = document.createElement('script');

    s.src = window.FT_ST_URL;

    s.onload = arm;

    document.head.appendChild(s);

  }



  var designMode = !!(window.Shopify && window.Shopify.designMode);

  var lite = html.classList.contains('ft-lite'); /* weak device — skip choreography */

  var GOLD_EASE = 'power3.out';

  var EXPO = 'expo.inOut';



  /* ---- FPS probe: if the first second runs slow, lock LITE in for

     the session so every next page starts cheap from first paint. */

  function probeFps() {

    if (lite || !mobileFast) return; /* desktop never auto-degrades */

    var frames = 0, t0 = performance.now();

    function tick(t) {

      frames++;

      if (t - t0 < 700) { requestAnimationFrame(tick); return; }

      var fps = frames / ((t - t0) / 1000);

      if (fps < 34) {

        try { sessionStorage.setItem('ftLite', '1'); } catch (e) {}

        html.classList.add('ft-lite'); /* CSS relief applies immediately */

      }

    }

    requestAnimationFrame(tick);

  }



  /* ---- UNIVERSAL: below-fold sections skip layout+paint until

     approached. Excludes sections with sticky/fixed UI, the

     dashboard, the atlas dial, and anything holding a video. */

  function initContentVis() {

    /* mobile-only: the contain-intrinsic-size estimate makes desktop

       scroll height shift as sections render in (visible jerks) */

    if (!mobileFast) return;

    var vh = window.innerHeight || 800;

    /* PERF: read every section top FIRST, then add classes — adding
       .ft-cv between reads forced one full-document style recalc per
       section (~30-90ms each at 4x CPU). Same result: sections are in
       flow, so once one sits >= 1.5vh every later one does too. */

    var secs = Array.prototype.slice.call(document.querySelectorAll('#MainContent > .shopify-section'));

    var tops = secs.map(function (sec) { return sec.getBoundingClientRect().top; });

    secs.forEach(function (sec, i) {

      if (sec.classList.contains('ft-cv')) return;

      if (tops[i] < vh * 1.5) return;

      if (sec.querySelector('product-form, video, [data-ds-section], [id^="aa-"], [class*="sticky"]')) return;

      sec.classList.add('ft-cv');

    });

  }



  /* ---- UNIVERSAL: autoplay + lazyplay videos run ONLY while on

     screen (lazyplay ones don't even buffer until first seen). */

  function initVideoDoze() {

    if (!('IntersectionObserver' in window)) return;

    var vids = document.querySelectorAll('video[autoplay], video[data-kx-lazyplay]');

    if (!vids.length) return;

    var io = new IntersectionObserver(function (entries) {

      entries.forEach(function (en) {

        var v = en.target;

        try {

          if (en.isIntersecting) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }

          else v.pause();

        } catch (e) {}

      });

    }, { rootMargin: '120px 0px' });

    vids.forEach(function (v) { io.observe(v); });

  }



  /* ---------------- zoom-guard integration ---------------- */

  window.addEventListener('ft:zoom-start', function () {

    gsap.globalTimeline.pause();

    if (stReady) ScrollTrigger.config({ autoRefreshEvents: 'visibilitychange,DOMContentLoaded,load' });

  });

  window.addEventListener('ft:zoom-end', function () {

    gsap.globalTimeline.resume();

    if (stReady) {

      ScrollTrigger.config({ autoRefreshEvents: 'visibilitychange,DOMContentLoaded,load,resize' });

      ScrollTrigger.refresh();

    }

  });



  /* ================= GOLD CURSOR HALO =================

     A ring that glides along with the NATIVE cursor. After two

     rounds of "I can't see my mouse", the rule is absolute: nothing

     ever sets cursor:none — the halo is additive decoration only. */

  function initCursor() {

    if (!finePointer || designMode) return;



    var ring = document.createElement('div');

    ring.className = 'kx-cursor';

    /* position layer (JS transform, NO transitions) wraps a visual

       orb (CSS scale transitions) — decoupled elements can never

       rubber-band against each other */

    ring.innerHTML = '<div class="kx-cursor__orb"></div>';

    document.body.appendChild(ring);



    var x = -100, y = -100, rx = -100, ry = -100;

    var rafId = null, live = false;



    function step() {

      rx += (x - rx) * 0.35;

      ry += (y - ry) * 0.35;

      ring.style.transform = 'translate(' + rx + 'px,' + ry + 'px)';

      if (Math.abs(x - rx) > 0.12 || Math.abs(y - ry) > 0.12) {

        rafId = requestAnimationFrame(step);

      } else {

        rx = x; ry = y;

        ring.style.transform = 'translate(' + rx + 'px,' + ry + 'px)';

        rafId = null;

      }

    }



    function wake() {

      if (!live) {

        live = true;

        ring.classList.add('is-live');

      }

      if (rafId === null) rafId = requestAnimationFrame(step);

    }



    function sleep() {

      live = false;

      ring.classList.remove('is-live');

    }



    document.addEventListener('mousemove', function (e) {

      x = e.clientX; y = e.clientY;

      wake();

    }, { passive: true });



    document.addEventListener('mouseleave', sleep);

    window.addEventListener('ft:zoom-start', sleep);

    document.addEventListener('visibilitychange', function () {

      if (document.hidden) sleep();

    });



    document.addEventListener('mousedown', function () { ring.classList.add('is-down'); });

    document.addEventListener('mouseup', function () { ring.classList.remove('is-down'); });



    /* ONE stable toggle — the old add-on-over/remove-on-out pair

       flip-flopped while crossing nested elements, restarting the

       scale transition every few ms (the "twerking"). mouseover

       alone fires on every element entry, so toggling against

       closest() is always correct and never flickers. */

    var hoverSel = 'a, button, [role="button"], input, textarea, select, label, summary, .swatch';

    document.addEventListener('mouseover', function (e) {

      var on = !!(e.target.closest && e.target.closest(hoverSel));

      ring.classList.toggle('is-hover', on);

    }, { passive: true });

  }



  /* ================= INTRO VEIL (once per session) ================= */

  function playIntro(done) {

    var veil = document.createElement('div');

    veil.className = 'kx-veil';

    /* the FULL official lockup: wordmark (with a light-sweep shine

       masked to the letterforms) + the "exuding divinity" script

       revealing with a left-to-right swipe. Pixels only, never

       retyped. Falls back to plain text if assets are missing. */

    if (window.FT_LOGO) {

      veil.innerHTML =

        '<div class="kx-veil__lockup">' +

          '<div class="kx-veil__wm">' +

            '<img class="kx-veil__logo" src="' + window.FT_LOGO + '" alt="Flowertrail" decoding="async">' +

            '<div class="kx-veil__shine" style="-webkit-mask-image:url(' + window.FT_LOGO + ');mask-image:url(' + window.FT_LOGO + ')"></div>' +

          '</div>' +

          (window.FT_LOGO_DIV

            ? '<img class="kx-veil__div" src="' + window.FT_LOGO_DIV + '" alt="exuding divinity" decoding="async">'

            : '') +

        '</div>' +

        '<div class="kx-veil__line"></div>';

    } else {

      veil.innerHTML = '<div class="kx-veil__mark">Flowertrail</div><div class="kx-veil__line"></div>';

    }

    document.body.appendChild(veil);

    clearBootCovers(); /* the veil element itself now covers the page */



    var mark = veil.querySelector('.kx-veil__logo, .kx-veil__mark');

    var shine = veil.querySelector('.kx-veil__shine');

    var script = veil.querySelector('.kx-veil__div');

    var line = veil.querySelector('.kx-veil__line');

    var lockup = veil.querySelector('.kx-veil__lockup') || mark;



    /* ABSOLUTE fail-safe: whatever happens to the timeline, the veil

       can never cover the page for more than 5 seconds */

    setTimeout(function () {

      if (veil.parentNode) {

        veil.style.transition = 'opacity 0.5s ease';

        veil.style.opacity = '0';

        setTimeout(function () { veil.remove(); }, 600);

      }

    }, 5000);



    var tl = gsap.timeline({

      onComplete: function () {

        veil.remove();

        if (done) done();

      }

    });

    /* 1 · wordmark settles in with a soft bloom */

    tl.fromTo(mark, { opacity: 0, scale: 0.94, y: 12 },

      { opacity: 1, scale: 1, y: 0, duration: 0.8, ease: GOLD_EASE });

    /* 2 · a band of light travels THROUGH the letterforms.

       The masked element stays STILL — only the gradient inside it

       moves (backgroundPosition). Moving the element itself slid the

       mask with it (a ghost wordmark) and re-rasterized every frame. */

    if (shine) {

      tl.fromTo(shine, { backgroundPosition: '130% 0', opacity: 1 },

        { backgroundPosition: '-130% 0', duration: 1.0, ease: 'power2.inOut' }, '-=0.35');

    }

    /* 3 · "exuding divinity" writes itself in — left-to-right swipe.

       (glow is static CSS — a filter-string tween here could stall

       the whole timeline and strand the veil over the page) */

    if (script) {

      tl.fromTo(script,

        { clipPath: 'inset(-10% 100% -10% 0%)', opacity: 1 },

        { clipPath: 'inset(-10% 0% -10% 0%)', duration: 0.9, ease: 'power2.out' }, '-=0.55');

    }

    /* 4 · gold hairline draws beneath, hold, then the curtain lifts */

    tl.fromTo(line, { scaleX: 0 }, { scaleX: 1, duration: 0.6, ease: EXPO }, '-=0.6')

      .to({}, { duration: 0.4 })

      .to([lockup, line], { opacity: 0, y: -12, duration: 0.3, ease: 'power2.in' })

      .to(veil, { clipPath: 'inset(0 0 100% 0)', duration: 0.65, ease: EXPO }, '-=0.05');

  }



  /* ============ PAGE TRANSITIONS: REMOVED (Aryan) ==============

     The blanket sweep never matched the Koenigsegg reference feel —

     navigation is now instant and native. FTKX.navigate stays as a

     plain redirect for the card/slider click handlers. */

  function wipeTo(dest) { window.location.href = dest; }



  /* ============ DRAWER EFFECTS (the menu panel sweep) ============

     MENU: a SOLID panel sweeps the screen with a hard 45-degree

     diagonal edge — in from the top-right, out toward bottom-left.

     (RESTORED 2026-07-17: the blanket removal accidentally excised

     these definitions, which killed the menu AND crashed start().) */

  var MENU_CLOSED_TR = 'polygon(150% -50%, 150% -50%, 150% -50%)';

  var MENU_FULL_TR = 'polygon(150% -50%, 150% 370%, -270% -50%)';

  var MENU_FULL_BL = 'polygon(-50% 150%, 370% 150%, -50% -270%)';

  var MENU_CLOSED_BL = 'polygon(-50% 150%, -50% 150%, -50% 150%)';



  function initDrawerFx() {

    var menu = document.querySelector('[data-drawer]');

    if (!menu) return;

    gsap.set(menu, { clipPath: MENU_CLOSED_TR });

    var wasOpen = menu.classList.contains('active');

    new MutationObserver(function () {

      var open = menu.classList.contains('active');

      if (open === wasOpen) return;

      wasOpen = open;

      gsap.killTweensOf(menu);

      if (open) {

        /* sweep IN: diagonal edge travels top-right -> bottom-left */

        gsap.fromTo(menu, { clipPath: MENU_CLOSED_TR },

          { clipPath: MENU_FULL_TR, duration: 0.7, ease: 'expo.inOut' });

      } else {

        /* sweep OUT: continue the motion off the bottom-left corner */

        gsap.fromTo(menu, { clipPath: MENU_FULL_BL },

          {

            clipPath: MENU_CLOSED_BL, duration: 0.6, ease: 'expo.inOut',

            onComplete: function () { gsap.set(menu, { clipPath: MENU_CLOSED_TR }); }

          });

      }

    }).observe(menu, { attributes: true, attributeFilter: ['class'] });

  }



  /* ============ CHROME AUTO-HIDE (RESTORED 2026-07-17) ============ */

  function initChrome() {

    var lastY = window.scrollY || 0;

    var raf = null;

    function apply() {

      raf = null;

      var y = window.scrollY || 0;

      if (html.classList.contains('ft-zooming')) { lastY = y; return; }

      if (y < 60) {

        html.classList.remove('kx-chrome-hide');

      } else if (y > lastY + 6) {

        html.classList.add('kx-chrome-hide');

      } else if (y < lastY - 6) {

        html.classList.remove('kx-chrome-hide');

      }

      lastY = y;

    }

    window.addEventListener('scroll', function () {

      if (raf === null) raf = requestAnimationFrame(apply);

    }, { passive: true });

  }



  /* ============ DIAGONAL REVEALS (RESTORED 2026-07-17) ============ */

  var DIAG_FROM = [

    'polygon(0% 0%, 0% 0%, 0% 0%)',

    'polygon(100% 100%, 100% 100%, 100% 100%)'

  ];

  var DIAG_TO = [

    'polygon(0% 0%, 320% 0%, 0% 320%)',

    'polygon(100% 100%, -220% 100%, 100% -220%)'

  ];



  function diagonalReveal(el, corner, dur) {

    gsap.set(el, { clipPath: DIAG_FROM[corner], opacity: 1 });

    ScrollTrigger.create({

      trigger: el,

      start: 'top 88%',

      once: true,

      onEnter: function () {

        gsap.to(el, {

          clipPath: DIAG_TO[corner],

          duration: dur || 0.95,

          ease: 'expo.out',

          clearProps: 'clipPath'

        });

      }

    });

  }



  function initDiagonals(root) {

    if (lite || mobileFast) return;

    if (!stReady) { withST(function () { initDiagonals(root); }); return; }

    var i = 0;

    var list = Array.prototype.slice.call((root || document).querySelectorAll(

      '#MainContent .card-wrapper, [data-kx-diag]'

    ));

    var below = list.map(belowFold); /* PERF: all reads before the gsap.set writes */

    list.forEach(function (el, n) {

      if (!eligible(el) || !below[n]) return;

      el.setAttribute('data-kx-done', '');

      diagonalReveal(el, i % 2, 0.95);

      i++;

    });

  }



  /* ============ ZIGZAG WIPES (RESTORED 2026-07-17) ============ */

  var WIPE_TARGETS = '.ft-aplus__module, .ft-aplus__carousel, .ft-vitrine__row, ' +

                     '.ftg-occasion, .ftg-scene, .ft-story .ftst-pillar, .ft-story img, ' +

                     '.ft-saga__visual, .hl .hl-vid, .hl .hl-card';

  function wipeDirLtr(el, i) {

    if (el.classList.contains('ft-vitrine__row')) return !el.classList.contains('is-rev');

    return i % 2 === 0;

  }



  function initBannerWipes(root) {

    if (lite || mobileFast) return;

    if (!stReady) { withST(function () { initBannerWipes(root); }); return; }

    var i = 0;

    var list = Array.prototype.slice.call((root || document).querySelectorAll(WIPE_TARGETS));

    var below = list.map(belowFold); /* PERF: all reads before the gsap.set writes */

    list.forEach(function (el, n) {

      if (el.hasAttribute('data-kx-done') || !below[n]) return;
      if (el.querySelector('[data-ft-live]')) return; /* live fluid cards: never clip/translate (the logo is centred by its own transform) */

      if (el.closest(OVERLAY_SCOPE)) return;

      el.setAttribute('data-kx-done', '');

      var ltr = wipeDirLtr(el, i);

      i++;

      gsap.set(el, { clipPath: ltr ? 'inset(0% 100% 0% 0%)' : 'inset(0% 0% 0% 100%)', opacity: 1 });

      var img = el.querySelector('img');

      if (img) gsap.set(img, { xPercent: ltr ? -6 : 6 });

      ScrollTrigger.create({

        trigger: el,

        start: 'top 86%',

        once: true,

        onEnter: function () {

          gsap.to(el, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.05, ease: 'expo.out', clearProps: 'clipPath' });

          if (img) gsap.to(img, { xPercent: 0, duration: 1.15, ease: 'expo.out', clearProps: 'transform' });

        }

      });

    });

  }



  /* internal-link click interception removed with the blanket —

     links navigate natively. bfcache cleanup keeps the veil safe. */

  window.addEventListener('pageshow', function (ev) {

    if (ev.persisted) {

      clearBootCovers();

      var v = document.querySelector('.kx-veil');

      if (v) v.remove();

    }

  });



  /* ================= SCROLL PROGRESS HAIRLINE (desktop) ============ */

  withST(function () {

    var progress = document.createElement('div');

    progress.className = 'kx-progress';

    document.body.appendChild(progress);

    gsap.to(progress, {

      scaleX: 1,

      ease: 'none',

      scrollTrigger: { start: 0, end: 'max', scrub: 0.4 }

    });

  });



  /* ============ MOBILE CHEAP REVEALS ============

     One shared IO; CSS transitions in ft-mobile-fast.css do the

     work. Covers the fade-rises AND the A+ banner L→R/R→L wipes. */

  var cheapIO = null;

  function initCheapReveals(root) {

    /* NOTE: lite devices still get the banner WIPES (clip-path CSS is

       ~free); only the per-card fade-rises are skipped under lite. */

    if (!mobileFast || !('IntersectionObserver' in window)) return;

    if (!cheapIO) {

      cheapIO = new IntersectionObserver(function (entries) {

        entries.forEach(function (en) {

          if (en.isIntersecting) {

            en.target.classList.add('kxm-in');

            cheapIO.unobserve(en.target);

          }

        });

      }, { rootMargin: '0px 0px 14% 0px' }); /* pre-trigger: reveal JUST before entering view */

    }

    /* PERF: READ PASS first — collect both candidate lists and measure
       every top before ANY class write. Adding kxm-pre/kxm-wipe between
       getBoundingClientRect calls forced a full-document style recalc
       per element (a multi-second frozen task on the PLP at 4x CPU).
       Same elements get the same classes in the same order. */

    var preC = [], wipeC = [];

    if (!lite) {

      (root || document).querySelectorAll(

        '#MainContent .card-wrapper, #MainContent .multicolumn-card, ' +

        '.footer .footer-block, #MainContent [data-kx-rise]'

      ).forEach(function (el) {

        if (eligible(el)) preC.push(el);

      });

    }

    (root || document).querySelectorAll(WIPE_TARGETS).forEach(function (el) {

      if (el.hasAttribute('data-kxm-w')) return;

      if (el.querySelector('[data-ft-live]')) return; /* live fluid cards: no mobile wipe either */

      wipeC.push(el);

    });

    var preBelow = preC.map(belowFold);

    var wipeBelow = wipeC.map(belowFold);

    /* WRITE PASS */

    preC.forEach(function (el, i) {

      if (!preBelow[i]) return;

      el.setAttribute('data-kx-done', '');

      el.classList.add('kxm-pre');

      cheapIO.observe(el);

    });

    /* wipes get their OWN observer that fires only once the banner is

       ~10% into the viewport — on the shared pre-trigger IO the sweep

       finished before the element was ever visible (looked like no

       effect at all on mobile) */

    if (!initCheapReveals.wipeIO) {

      initCheapReveals.wipeIO = new IntersectionObserver(function (entries) {

        entries.forEach(function (en) {

          if (en.isIntersecting) {

            en.target.classList.add('kxm-in');

            initCheapReveals.wipeIO.unobserve(en.target);

          }

        });

      }, { rootMargin: '0px 0px -10% 0px' });

    }

    var wipeIdx = 0;

    wipeC.forEach(function (el, i) {

      el.setAttribute('data-kxm-w', '');

      if (!wipeBelow[i]) return;

      el.classList.add('kxm-wipe');

      if (!wipeDirLtr(el, wipeIdx)) el.classList.add('kxm-alt');

      wipeIdx++;

      initCheapReveals.wipeIO.observe(el);

    });

  }



  /* ================= SCROLL REVEALS ================= */

  var OVERLAY_SCOPE = [

    'dialog', 'details', 'cart-drawer', 'menu-drawer', 'modal-dialog',

    'quick-add-modal', 'predictive-search', '.mobile-facets__wrapper',

    '[hidden]', '[class*="modal"]', '[class*="drawer"]',

    '[class*="lightbox"]', '[class*="popup"]', '[class*="tooltip"]'

  ].join(',');



  /* never hide what the visitor can already see — reveal choreography

     is for BELOW-fold content only (hiding above-fold content until

     JS wakes up tanks perceived load speed on slow devices) */

  function belowFold(el) {

    return el.getBoundingClientRect().top > (window.innerHeight || 800) * 0.95;

  }



  function eligible(el) {

    if (el.hasAttribute('data-kx-done')) return false;

    if (el.classList.contains('ft-reveal')) return false;     /* ft-motion owns these */

    if (el.classList.contains('scroll-trigger')) return false; /* Dawn's reveal owns these */

    if (el.closest(OVERLAY_SCOPE)) return false;

    return true;

  }



  function initHeadings(root) {

    if (lite || mobileFast) return;

    if (!stReady) { withST(function () { initHeadings(root); }); return; }

    var els = [];

    (root || document).querySelectorAll(

      '#MainContent h1, #MainContent h2, #MainContent .banner__heading, #MainContent .title-wrapper .title'

    ).forEach(function (el) {

      if (eligible(el) && belowFold(el)) { el.setAttribute('data-kx-done', ''); els.push(el); }

    });

    els.forEach(function (el) {

      gsap.set(el, { clipPath: 'inset(0 0 102% 0)', y: 26 });

      ScrollTrigger.create({

        trigger: el,

        start: 'top 88%',

        once: true,

        onEnter: function () {

          gsap.to(el, {

            clipPath: 'inset(0 0 -8% 0)', y: 0,

            duration: 0.95, ease: GOLD_EASE, clearProps: 'clipPath,transform'

          });

        }

      });

    });

  }



  function initRises(root) {

    if (lite || mobileFast) return;

    if (!stReady) { withST(function () { initRises(root); }); return; }

    var els = [];

    (root || document).querySelectorAll(

      '#MainContent .multicolumn-card, ' +

      '.footer .footer-block, #MainContent [data-kx-rise]'

    ).forEach(function (el) {

      if (eligible(el) && belowFold(el)) { el.setAttribute('data-kx-done', ''); els.push(el); }

    });

    if (!els.length) return;

    gsap.set(els, { opacity: 0, y: 34 });

    ScrollTrigger.batch(els, {

      start: 'top 92%',

      once: true,

      onEnter: function (batch) {

        gsap.to(batch, {

          opacity: 1, y: 0,

          duration: 0.9, ease: GOLD_EASE,

          stagger: 0.08, clearProps: 'opacity,transform'

        });

      }

    });

  }



  /* A+ / vitrine media: cinematic unmask — image settles from 1.06

     scale while the clip opens. Marked kx-unmask in section markup. */

  function initUnmasks(root) {

    if (lite || mobileFast) return;

    if (!stReady) { withST(function () { initUnmasks(root); }); return; }

    var list = Array.prototype.slice.call((root || document).querySelectorAll('.kx-unmask'));

    var below = list.map(belowFold); /* PERF: all reads before the gsap.set writes */

    list.forEach(function (el, n) {

      if (!eligible(el) || !below[n]) return;

      el.setAttribute('data-kx-done', '');

      var img = el.querySelector('img');

      gsap.set(el, { clipPath: 'inset(6% 4% 6% 4%)', opacity: 0.001 });

      if (img) gsap.set(img, { scale: 1.06 });

      ScrollTrigger.create({

        trigger: el,

        start: 'top 86%',

        once: true,

        onEnter: function () {

          var tl = gsap.timeline();

          tl.to(el, { opacity: 1, duration: 0.2, ease: 'none' })

            .to(el, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.05, ease: EXPO, clearProps: 'clipPath' }, 0);

          if (img) tl.to(img, { scale: 1, duration: 1.3, ease: GOLD_EASE, clearProps: 'transform' }, 0);

        }

      });

    });

  }



  /* ================= PARALLAX ================= */

  function initParallax(root) {

    if (lite || mobileFast) return; /* scroll-linked work is jank fuel off desktop */

    if (!stReady) { withST(function () { initParallax(root); }); return; }

    (root || document).querySelectorAll(

      '#MainContent .banner__media img, [data-kx-parallax]'

    ).forEach(function (el) {

      if (el.hasAttribute('data-kx-plx-on')) return;

      if (el.closest(OVERLAY_SCOPE)) return;

      el.setAttribute('data-kx-plx-on', '');

      var holder = el.parentElement;

      if (!holder) return;

      gsap.fromTo(el,

        { yPercent: -6, scale: 1.14 },

        {

          yPercent: 6, scale: 1.14, ease: 'none',

          scrollTrigger: { trigger: holder, start: 'top bottom', end: 'bottom top', scrub: 0.6 }

        });

    });

  }



  /* ================= MAGNETIC CTAs (fine pointer only) ============= */

  function initMagnetic(root) {

    if (!finePointer || lite) return;

    (root || document).querySelectorAll(

      '#MainContent .button, #MainContent [data-kx-magnetic]'

    ).forEach(function (btn) {

      if (btn.hasAttribute('data-kx-mag-on')) return;

      if (btn.closest(OVERLAY_SCOPE)) return;

      btn.setAttribute('data-kx-mag-on', '');

      var qx = gsap.quickTo(btn, 'x', { duration: 0.4, ease: 'power3.out' });

      var qy = gsap.quickTo(btn, 'y', { duration: 0.4, ease: 'power3.out' });

      btn.addEventListener('pointermove', function (e) {

        var r = btn.getBoundingClientRect();

        if (!r.width) return;

        qx(((e.clientX - r.left) / r.width - 0.5) * 10);

        qy(((e.clientY - r.top) / r.height - 0.5) * 8);

      }, { passive: true });

      btn.addEventListener('pointerleave', function () { qx(0); qy(0); }, { passive: true });

    });

  }



  /* ================= CAROUSELS ([data-kx-carousel]) =================

     Two modes:

     · data-kx-sweep — THE reference interaction (recording t=30s):

       the current panel stays perfectly still and the INCOMING panel

       sweeps diagonally across it (next: from top-right; prev: from

       bottom-left). Buttons + touch swipe.

     · default — plain scroll-snap strip. */

  function initCarousels(root) {

    (root || document).querySelectorAll('[data-kx-carousel]').forEach(function (wrap) {

      if (wrap.hasAttribute('data-kx-car-on')) return;

      wrap.setAttribute('data-kx-car-on', '');

      var track = wrap.querySelector('[data-kx-track]');

      if (!track) return;



      if (wrap.hasAttribute('data-kx-sweep') && window.gsap) {

        var panels = Array.prototype.slice.call(track.children);

        if (panels.length < 2) return;

        /* the panels are lazy imgs — force-load them all NOW so the

           sweep never reveals a half-loaded infographic (Aryan: slow) */

        function forceLoad() {

          panels.forEach(function (p) {

            p.querySelectorAll('img').forEach(function (im) {

              im.loading = 'eager';

              try { if (im.decode) im.decode().catch(function () {}); } catch (e) {}

            });

          });

        }

        /* PERF: force them only once the carousel is within ~1.5 screens
           (it sits several screens down the PDP) — first view no longer
           pays ~375KB for it, and panels still finish loading long
           before the sweep can show them. 150% not 200%: during boot
           the carousel can sit ~375px higher than its settled spot
           (2730 vs 3105px on a 915px phone), which 200% caught in
           about 40% of loads. */

        if ('IntersectionObserver' in window) {

          var eagerIO = new IntersectionObserver(function (entries) {

            if (!entries.some(function (en) { return en.isIntersecting; })) return;

            eagerIO.disconnect();

            forceLoad();

          }, { rootMargin: '150% 0px' });

          eagerIO.observe(wrap);

        } else {

          forceLoad();

        }

        var cur = 0, busy = false;

        panels.forEach(function (p, i) {

          p.style.visibility = i === 0 ? 'visible' : 'hidden';

          p.style.zIndex = i === 0 ? '1' : '0';

        });



        function show(next, fromTR) {

          next = (next + panels.length) % panels.length;

          if (busy || next === cur) return;

          var inc = panels[next], out = panels[cur];

          if (lite || html.classList.contains('ft-lite')) {

            /* weak device: clean instant swap */

            inc.style.visibility = 'visible'; inc.style.zIndex = '1';

            out.style.visibility = 'hidden'; out.style.zIndex = '0';

            cur = next;

            return;

          }

          busy = true;

          inc.style.visibility = 'visible';

          inc.style.zIndex = '2';

          out.style.zIndex = '1';

          gsap.fromTo(inc,

            { clipPath: fromTR ? MENU_CLOSED_TR : MENU_CLOSED_BL },

            {

              clipPath: fromTR ? MENU_FULL_TR : MENU_FULL_BL,

              duration: 0.8,

              ease: 'expo.inOut',

              onComplete: function () {

                out.style.visibility = 'hidden';

                out.style.zIndex = '0';

                inc.style.zIndex = '1';

                gsap.set(inc, { clearProps: 'clipPath' });

                cur = next;

                busy = false;

              }

            });

        }



        wrap.querySelectorAll('[data-kx-prev],[data-kx-next]').forEach(function (btn) {

          btn.addEventListener('click', function () {

            var fwd = btn.hasAttribute('data-kx-next');

            show(cur + (fwd ? 1 : -1), fwd);

          });

        });

        var tx = null;

        track.addEventListener('touchstart', function (e) {

          tx = e.touches && e.touches.length === 1 ? e.touches[0].clientX : null;

        }, { passive: true });

        track.addEventListener('touchend', function (e) {

          if (tx === null) return;

          var dx = e.changedTouches[0].clientX - tx;

          tx = null;

          if (Math.abs(dx) > 40) {

            var fwd = dx < 0;

            show(cur + (fwd ? 1 : -1), fwd);

          }

        }, { passive: true });

        return;

      }



      /* default: scroll-snap strip */

      wrap.querySelectorAll('[data-kx-prev],[data-kx-next]').forEach(function (btn) {

        btn.addEventListener('click', function () {

          var item = track.firstElementChild;

          if (!item) return;

          var step = item.getBoundingClientRect().width + 1;

          track.scrollBy({

            left: btn.hasAttribute('data-kx-prev') ? -step : step,

            behavior: 'smooth'

          });

        });

      });

    });

  }



  /* ============ SCROLL CUE (under the lotus) ============

     A small wordless invitation, bottom-center of the hero: a gold

     hairline with a bright segment sweeping vertically in a loop —

     "scroll here". Shows only while the lotus hero is on screen. */

  function initLotusRail() {

    var sec = document.querySelector('[id$="__luxury_hero_banner"]');

    if (!sec) return;

    var cue = document.createElement('div');

    /* PERF: starts kx-idle (sweep paused); update() wakes it the moment
       it turns on. Same first frame as before — the cue is inserted at
       opacity 0 either way. */

    cue.className = 'kx-scroll-cue kx-idle';

    cue.innerHTML = '<div class="kx-scroll-cue__seg"></div>';

    document.body.appendChild(cue);

    var raf = null, idleT = null;

    function update() {

      raf = null;

      var r = sec.getBoundingClientRect();

      var vh = window.innerHeight;

      var on = r.bottom > vh * 0.55 && r.top < vh * 0.4;

      cue.classList.toggle('is-on', on);

      /* PERF: pause the infinite sweep once the cue is invisible — but
         only AFTER its 0.5s fade-out, so the segment never freezes
         mid-fade (the look stays identical) */

      if (on) {

        clearTimeout(idleT); idleT = null;

        cue.classList.remove('kx-idle');

      } else if (idleT === null && !cue.classList.contains('kx-idle')) {

        idleT = setTimeout(function () { idleT = null; cue.classList.add('kx-idle'); }, 550);

      }

    }

    function onScroll() { if (raf === null) raf = requestAnimationFrame(update); }

    window.addEventListener('scroll', onScroll, { passive: true });

    window.addEventListener('resize', onScroll, { passive: true });

    update();

  }



  /* ================= BOOT ================= */

  function initAll(root) {

    if (!designMode) {

      safe(initHeadings, root);

      safe(initRises, root);

      safe(initBannerWipes, root); /* wipes claim their targets first */

      safe(initDiagonals, root);

      safe(initUnmasks, root);

      safe(initParallax, root);

      safe(initMagnetic, root);

      safe(initCheapReveals, root); /* the mobile path */

    }

    safe(initCarousels, root);

  }



  /* one failing subsystem must NEVER kill the others */

  function safe(fn, root) {

    try { fn(root); } catch (e) {

      if (window.console && console.warn) console.warn('FTKX:', e);

    }

  }



  function start() {

    safe(initCursor);

    if (!designMode) { safe(initDrawerFx); safe(initChrome); }

    if (designMode) {

      clearBootCovers();

    } else if (html.classList.contains('kx-intro-pending') && finePointer && !lite) {

      /* the intro veil is a desktop indulgence — on mobile, speed IS luxury */

      safe(playIntro);

    } else {

      clearBootCovers();

    }

    safe(loadST); /* desktop: begin fetching ScrollTrigger; phones skip */

    safe(initLotusRail);

    initAll(document);

    safe(initVideoDoze); /* at DCL, not load — pause off-screen videos BEFORE they buffer fully */

    window.addEventListener('load', function () {

      if (stReady) ScrollTrigger.refresh();

      safe(initContentVis);

      safe(probeFps);

    }, { once: true });

  }



  if (document.readyState === 'loading') {

    document.addEventListener('DOMContentLoaded', start, { once: true });

  } else {

    start();

  }



  /* Shopify theme editor: re-run for re-rendered sections */

  document.addEventListener('shopify:section:load', function (e) {

    initAll(e.target);

    if (stReady) ScrollTrigger.refresh();

  });



  window.FTKX = {

    refresh: function () { initAll(document); if (stReady) ScrollTrigger.refresh(); },

    /* programmatic navigation with the diagonal sweep — used by the

       whole-card-clickable and slider-click handlers in theme.liquid */

    navigate: wipeTo

  };

})();

